import { classNames } from 'libs/react';
import { Image } from 'components/Image';
import { React } from 'libs/react';
import { useHistory, useLocation } from 'react-router-dom';
import i18n from 'components/translation/i18n';
import { useDomain } from 'components/domain/DomainProvider';
import './StoreHeader.scss';
import { storeHomePath } from './storePath';
import { readStoreHeadline, storeDocumentTitle } from './storeDocumentTitle';
import { imageSources, LOGO_SIZES, thumbnailSource } from 'helpers/mediaSources';
/**
 * Turns the account website into an http(s) URL, or null when it is empty
 * or not a navigable address.
 */
function accountWebsiteHref(website) {
    const value = website === null || website === void 0 ? void 0 : website.trim();
    if (!value) {
        return null;
    }
    const withProtocol = /^https?:\/\//i.test(value) ? value : `https://${value}`;
    try {
        const url = new URL(withProtocol);
        if (url.protocol !== 'http:' && url.protocol !== 'https:') {
            return null;
        }
        return url.href;
    }
    catch (_a) {
        return null;
    }
}
/**
 * @component StoreHeader
 * @since 1.0.0
 */
export function StoreHeader(props) {
    var _a;
    const { configurationFields, logo, partnerSlug, currentStepId = 'products', steps, isSuccessScreen = false, onStepClick, onLogoClick } = props;
    const history = useHistory();
    const location = useLocation();
    const { name, icon, website } = useDomain();
    const accountSiteHref = accountWebsiteHref(website);
    // Compact header: a partner store shows its own logo, else the account icon.
    const smallLogo = [partnerSlug ? logo : null, icon, logo].find((media) => media === null || media === void 0 ? void 0 : media.url);
    const language = i18n.language.substring(0, 2);
    const storeHomeHref = `/${storeHomePath(language, partnerSlug)}`;
    document.title =
        (_a = props.documentTitle) !== null && _a !== void 0 ? _a : storeDocumentTitle({
            businessName: name,
            headline: readStoreHeadline(configurationFields, language)
        });
    const currentStepIndex = steps.findIndex((step) => step.id === currentStepId);
    const currentStepNumber = currentStepIndex >= 0 ? currentStepIndex + 1 : 1;
    const activeStep = steps[currentStepIndex];
    const progressPercent = isSuccessScreen
        ? 100
        : Math.round((currentStepNumber / steps.length) * 100);
    const scrollToTop = () => {
        var _a;
        for (const selector of ['.store-screen', '.public-layout']) {
            (_a = document.querySelector(selector)) === null || _a === void 0 ? void 0 : _a.scrollTo({
                top: 0,
                left: 0,
                behavior: 'smooth'
            });
        }
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    };
    const goToStoreHome = (event) => {
        if (event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey ||
            event.button !== 0) {
            return;
        }
        event.preventDefault();
        if (onLogoClick) {
            onLogoClick();
            return;
        }
        if (location.pathname === storeHomeHref && !location.search) {
            scrollToTop();
            return;
        }
        history.push(storeHomeHref);
    };
    const className = classNames('store-header', props.className, {
        'store-header--success': isSuccessScreen
    });
    return (React.createElement("div", { className: className },
        React.createElement("div", { className: "store-header-main" },
            React.createElement("div", { className: "store-header-head" },
                React.createElement("a", { href: accountSiteHref !== null && accountSiteHref !== void 0 ? accountSiteHref : storeHomeHref, className: "store-header-logo-button", "aria-label": accountSiteHref ? name : t('Back to store'), onClick: accountSiteHref ? undefined : goToStoreHome },
                    React.createElement(Image, Object.assign({ eager: true, className: "store-header-logo" }, ((logo === null || logo === void 0 ? void 0 : logo.url)
                        ? imageSources(logo, LOGO_SIZES)
                        : { source: '/images/menu-bar-logo-icon.svg' }))),
                    React.createElement(Image, { eager: true, className: "store-header-icon", source: smallLogo
                            ? thumbnailSource(smallLogo)
                            : '/images/menu-bar-logo-icon.svg' }))),
            !isSuccessScreen && (React.createElement("div", { className: "store-header-body" },
                React.createElement("div", { className: "store-header-progress-mobile", "aria-label": t('Checkout progress') },
                    React.createElement("span", { className: "store-header-progress-mobile__text" },
                        `${t('Step')} ${currentStepNumber} ${t('of')} ${steps.length}`,
                        ' — ', activeStep === null || activeStep === void 0 ? void 0 :
                        activeStep.label),
                    React.createElement("div", { className: "store-header-progress-mobile__track", role: "progressbar", "aria-valuenow": progressPercent, "aria-valuemin": 0, "aria-valuemax": 100 },
                        React.createElement("div", { className: "store-header-progress-mobile__fill", style: { width: `${progressPercent}%` } }))),
                React.createElement("nav", { className: "store-header-steps", "aria-label": t('Checkout progress') }, steps.flatMap((step, index) => {
                    const isActive = currentStepId === step.id;
                    const isCompleted = currentStepIndex > index;
                    const items = [];
                    if (index > 0) {
                        items.push(React.createElement("div", { key: `connector-${step.id}`, className: classNames('store-header-step-connector', {
                                'is-completed': currentStepIndex >= index
                            }), "aria-hidden": "true" }));
                    }
                    const isClickable = isCompleted && !!onStepClick;
                    items.push(React.createElement("div", { key: step.id, className: classNames('store-header-step', {
                            'is-active': isActive,
                            'is-completed': isCompleted,
                            'is-pending': !isActive && !isCompleted,
                            'is-clickable': isClickable
                        }), "aria-current": isActive ? 'step' : undefined, role: isClickable ? 'button' : undefined, tabIndex: isClickable ? 0 : undefined, onClick: isClickable ? () => onStepClick(step.id) : undefined, onKeyDown: isClickable
                            ? (e) => e.key === 'Enter' && onStepClick(step.id)
                            : undefined },
                        React.createElement("div", { className: "store-header-step-circle" }, index + 1),
                        React.createElement("div", { className: "store-header-step-label" }, step.label)));
                    return items;
                })))))));
}
